"use client";

import { useEffect, useState } from "react";
import {
  CircleCheckIcon,
  CircleXIcon,
  InfoIcon,
  Loader2Icon,
  TriangleAlertIcon,
} from "lucide-react";
import { Toaster as Sonner, type ToasterProps } from "sonner";

function useDocumentTheme(): NonNullable<ToasterProps["theme"]> {
  const [theme, setTheme] = useState<NonNullable<ToasterProps["theme"]>>("light");

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => {
      setTheme(root.classList.contains("dark") ? "dark" : "light");
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  return theme;
}

export function Toaster(props: ToasterProps) {
  const theme = useDocumentTheme();

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      position="top-center"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <CircleXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {

          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--foreground)",
          "--normal-border": "var(--foreground)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {

          success:
            "[--normal-bg:color-mix(in_oklab,var(--color-green-50)_70%,var(--popover))] [--normal-border:var(--color-green-600)] [--normal-text:var(--color-green-700)] dark:[--normal-bg:color-mix(in_oklab,var(--color-green-950)_45%,var(--popover))] dark:[--normal-border:var(--color-green-500)] dark:[--normal-text:var(--color-green-300)]",
          error:
            "[--normal-bg:color-mix(in_oklab,var(--color-red-50)_70%,var(--popover))] [--normal-border:var(--color-red-600)] [--normal-text:var(--color-red-700)] dark:[--normal-bg:color-mix(in_oklab,var(--color-red-950)_45%,var(--popover))] dark:[--normal-border:var(--color-red-500)] dark:[--normal-text:var(--color-red-300)]",

          warning:
            "[--normal-bg:color-mix(in_oklab,var(--color-amber-50)_70%,var(--popover))] [--normal-border:var(--color-amber-600)] [--normal-text:var(--color-amber-700)] dark:[--normal-bg:color-mix(in_oklab,var(--color-amber-950)_45%,var(--popover))] dark:[--normal-border:var(--color-amber-500)] dark:[--normal-text:var(--color-amber-300)]",

          info:
            "[--normal-bg:color-mix(in_oklab,var(--color-blue-50)_70%,var(--popover))] [--normal-border:var(--color-blue-600)] [--normal-text:var(--color-blue-700)] dark:[--normal-bg:color-mix(in_oklab,var(--color-blue-950)_45%,var(--popover))] dark:[--normal-border:var(--color-blue-500)] dark:[--normal-text:var(--color-blue-300)]",

          loading:
            "[--normal-border:var(--border)] [--normal-text:var(--muted-foreground)]",
          description: "opacity-80",
        },
      }}
      {...props}
    />
  );
}
